import React, { useContext, useState, useRef } from 'react';
import { string } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Row } from 'simple-flexbox';
import { createUseStyles, useTheme } from 'react-jss';

import SLUGS from '../../resources/slugs';
import NAV_SEARCH_ITEMS from '../../resources/navSearchItems';
import { AuthContext } from '../../context/logeduser'; // Import AuthContext
import { IconButton, Typography, Box, Button, Tooltip, Autocomplete, TextField } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SearchIcon from '@mui/icons-material/Search';

import DropdownComponent from '../../components/dropdown';
import SidebarComponent from '../sidebar/SidebarComponent';
import { useCompany } from '../../context/CompanyContext';

const useStyles = createUseStyles((theme) => ({
  avatar: {
    height: 40,
    width: 40,
    borderRadius: '50%',
    border: `2px solid #3DAC5A`,
    boxShadow: '0 2px 8px rgba(61, 172, 90, 0.2)',
  },
  container: {
    height: 64,
    // Solid white rail header with a soft hairline border — matches the clean
    // employee-portal header (no glassmorphism), consistent across desktop + mobile.
    backgroundColor: '#fff',
    borderBottom: '1px solid #eef0f4',
  },
  name: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#1e293b',
    marginRight: '12px',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: '#1e293b',
    '@media (max-width: 600px)': {
      fontSize: '1rem',
    },
  }
}));

function HeaderComponent({ isDrawerOpen, setIsDrawerOpen }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles({ theme });
  const company = useCompany();
  const [logoRetry, setLogoRetry] = useState(0);
  React.useEffect(() => setLogoRetry(0), [company.logo]);
  const logoSrc = logoRetry > 0 && company.logo
    ? `${company.logo}${company.logo.includes('?') ? '&' : '?'}retry=${logoRetry}`
    : company.logo;
  const [sidebarVisible] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const searchInputRef = useRef(null);
  const { authData } = useContext(AuthContext); // Use AuthContext to get the authentication data
  const userName = authData?.admin?.user_name || 'Hr-Admin';
  // console.log(authData?.admin?.user_name || authData);


  const { logout } = useContext(AuthContext); // Get logout function from AuthContext
  const toggleDrawer = () => setIsDrawerOpen((prev) => !prev);
  // let title;
  // switch (true) {
  //   case currentItem === SLUGS.dashboard:
  //     title = 'Ats';
  //     break;
  //   case [SLUGS.overview, SLUGS.overviewTwo, SLUGS.overviewThree].includes(
  //     currentItem
  //   ):
  //     title = 'Overview';
  //     break;
  //   case currentItem === SLUGS.tickets:
  //     title = 'Tickets';
  //     break;
  //   case [SLUGS.ideas, SLUGS.ideasTwo, SLUGS.ideasThree].includes(currentItem):
  //     title = 'Ideas';
  //     break;
  //   case currentItem === SLUGS.contacts:
  //     title = 'Contacts';
  //     break;
  //   case currentItem === SLUGS.agents:
  //     title = 'Agents';
  //     break;
  //   case currentItem === SLUGS.articles:
  //     title = 'Articles';
  //     break;
  //   case currentItem === SLUGS.subscription:
  //     title = 'Subscription';
  //     break;
  //   case currentItem === SLUGS.settings:
  //     title = 'Settings';
  //     break;
  //   default:
  //     title = '';
  // }



  function handleLogout() {
    logout(); // Call the logout function
    navigate('/login'); // Redirect to the login page
  }

  return (
    <div className={classes.container} style={{ position: 'fixed', width: '100%', top: 0, zIndex: 1301 }}>
      <Row
        style={{
          height: '64px',
          paddingLeft: '25px',
          paddingRight: '30px',
        }}
        vertical="center"
        horizontal="space-between"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Tooltip title={isDrawerOpen ? 'Collapse sidebar' : 'Expand sidebar'} arrow>
            <IconButton
              onClick={toggleDrawer}
              aria-label={isDrawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              // Hidden on desktop (the rail is fixed-open there); shown only on
              // mobile, where it toggles the off-canvas drawer.
              sx={{ color: '#6E6B7B', display: { xs: 'inline-flex', md: 'none' }, width: 44, height: 44 }}
            >
              {isDrawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <span
            style={{ display: 'inline' }}
            className={classes.title}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 120 }}>
              {!company.loading && (
                <>
                  {company.logo && logoRetry < 3 && (
                    <img
                      style={{ display: 'inline', width: 'auto', height: '32px' }}
                      src={logoSrc}
                      alt={company.name || 'Company logo'}
                      onError={() => setLogoRetry((attempt) => Math.min(attempt + 1, 3))}
                    />
                  )}
                  <Typography sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', whiteSpace: 'nowrap' }}>
                    {company.shortName || company.name || company.code || 'HR'} <span style={{ color: '#3DAC5A' }}>HR</span>
                  </Typography>
                </>
              )}
            </Box>
          </span>
        </div>
        <Row vertical="center">
          {/* <div
            style={{
              color: '#8e8e8e',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              display: window.innerWidth < 600 ? 'none' : 'flex', // Hides when screen width is less than 600px
            }}
          >
            <SearchIcon sx={{ color: '#8e8e8e' }} />
          </div>
          <div
            className={classes.iconStyles}
            style={{
              color: '#8e8e8e',
              display: window.innerWidth < 600 ? 'none' : 'block', // Hides when screen width is less than 600px
            }}
          >
            <DropdownComponent
              label={<CircleNotificationsIcon sx={{ color: '#8e8e8e' }} />}
              options={[
                {
                  label: 'Notification #1',
                  onClick: () => // console.log('Notification #1'),
                },
                {
                  label: 'Notification #2',
                  onClick: () => // console.log('Notification #2'),
                },
                {
                  label: 'Notification #3',
                  onClick: () => // console.log('Notification #3'),
                },
                {
                  label: 'Notification #4',
                  onClick: () => // console.log('Notification #4'),
                },
              ]}
              position={{ top: 42, right: -14 }}
            />
          </div> */}



          <Autocomplete
            size="small"
            options={NAV_SEARCH_ITEMS}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.label}
            // Only show the results popup once the user has actually typed
            // something — don't dump the full page list open on a bare click.
            open={searchInput.length > 0}
            inputValue={searchInput}
            // MUI fires onInputChange a second time right after a selection
            // (reason: 'reset'), re-syncing the visible text to the picked
            // option's label — which was overwriting the manual clear below.
            // Only react to actual typing.
            onInputChange={(event, value, reason) => {
              if (reason === 'input') setSearchInput(value);
            }}
            filterOptions={(options, state) =>
              options.filter((o) => o.label.toLowerCase().includes(state.inputValue.toLowerCase()))
            }
            onChange={(event, value) => {
              if (value) navigate(value.route);
              setSearchInput('');
              searchInputRef.current?.blur();
            }}
            popupIcon={null}
            sx={{
              width: 220,
              mr: 2,
              display: { xs: 'none', md: 'block' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#f8f9fb',
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(node) => {
                  searchInputRef.current = node;
                  if (typeof params.inputRef === 'function') params.inputRef(node);
                  else if (params.inputRef) params.inputRef.current = node;
                }}
                placeholder="Search pages…"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ color: '#9CA3AF', fontSize: 20, ml: 0.5, mr: 0.5 }} />,
                }}
              />
            )}
          />

          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate(SLUGS.createemp)}
            sx={{
              mr: 2,
              bgcolor: '#4f46e5',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              padding: '8px 20px',
              boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.39)',
              '&:hover': {
                bgcolor: '#4338ca',
                boxShadow: '0 6px 20px rgba(79, 70, 229, 0.45)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s',
              display: { xs: 'none', md: 'flex' }
            }}
          >
            Add Employee
          </Button>

          <DropdownComponent
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { opacity: 0.8 } }}>
                <div className={classes.avatar} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eef2ff', color: '#4f46e5', fontWeight: 700, height: 40, width: 40, border: '2px solid #e0e7ff' }}>
                  {userName.charAt(0).toUpperCase()}
                </div>
              </Box>
            }
            position={{ top: 52, right: -6 }}
          />
          <IconButton
            onClick={handleLogout}
            sx={{
              ml: 2,
              color: '#ef4444',
              bgcolor: 'rgba(239, 68, 68, 0.05)',
              '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.15)', transform: 'scale(1.1)' },
              transition: 'all 0.2s'
            }}
          >
            <PowerSettingsNewIcon fontSize="small" />
          </IconButton>
        </Row>
      </Row>
      {sidebarVisible && <SidebarComponent />}
    </div>
  );
}

HeaderComponent.propTypes = {
  title: string,
};

export default HeaderComponent;
